import DynamicImage from '@/components/commons/dynamic-image';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn, isEmpty, truncateText } from '@/lib/utils';
import { ThemeColorSchemeType } from '@/stores/builder';
import { BaseFreeGift } from '@/stores/promotion';
import {
  BasePromotionRewardProduct,
  BasePromotionRewardProductItem,
} from '@/types/http/promotion/common';
import { MinusIcon, PlusIcon, Search } from 'lucide-react';
import { useState } from 'react';

type TabProductItemProps = {
  params: {
    page: number;
    pageSize: number;
    q: string;
  };
  handleSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isLoading: boolean;
  listProductItem: BasePromotionRewardProductItem[];
  colorScheme?: ThemeColorSchemeType;
  selectedProduct: BasePromotionRewardProduct;
  handleUpdateQty: (
    productGroup: BasePromotionRewardProduct,
    item: BasePromotionRewardProductItem,
    newQty: number
  ) => void;
  selectedProducts: BaseFreeGift[];
  maxQty: number;
  totalQty: number;
};

export default function TabProductItem({
  params,
  handleSearch,
  isLoading,
  listProductItem,
  colorScheme,
  selectedProduct,
  handleUpdateQty,
  selectedProducts,
  maxQty,
  totalQty,
}: TabProductItemProps) {
  const [selectedItem, setSelectedItem] = useState<BasePromotionRewardProductItem | undefined>();

  const getItemQty = (productGroupId: string, itemId: string) => {
    const group = selectedProducts.find(
      (g) => g.product_group?.product_group_id === productGroupId
    );
    const item = group?.items.find((i) => i.item_id === itemId);
    return item?.qty || 0;
  };

  const handleClickItem = (item: BasePromotionRewardProductItem) => {
    const currentSelectedId = selectedItem?.item_id;
    const clickedItemId = item.item_id;

    if (currentSelectedId === clickedItemId) {
      setSelectedItem(undefined);
    } else {
      setSelectedItem(item);
    }
  };

  return (
    <div
      className='relative flex max-h-[500px] w-full flex-col gap-5 overflow-y-auto sm:flex-row sm:overflow-hidden'
      style={
        {
          '--text-primary': colorScheme?.text || '#000000',
          '--text-secondary': colorScheme?.textSecondary || '#6B7280',
          '--background': colorScheme?.background || '#FFFFFF',
          '--btn-background': colorScheme?.btnSolidBackgroud || '#000000',
          '--btn-label': colorScheme?.btnSolidlabel || '#FFFFFF',
        } as React.CSSProperties
      }
    >
      <div className='w-full max-w-full border-t lg:max-w-[30%] lg:border-r'>
        <div className='flex w-full flex-col px-6 py-4'>
          <DynamicImage
            src={
              selectedItem?.product_assets?.[0] || (selectedProduct?.product_assets?.[0] as string)
            }
            alt={selectedProduct?.product_name as string}
            width={500}
            height={500}
            className='aspect-square rounded-sm object-cover'
          />
          <div className='px-2 py-4'>
            <h4 className='line-clamp-2 break-words text-base font-medium leading-tight text-[--text-primary]'>
              {selectedProduct?.product_name}
            </h4>
            <span className='text-sm font-normal text-[--text-secondary]'>
              Stock: {selectedItem?.stock ? selectedItem?.stock : selectedProduct.stock}
            </span>
          </div>
          <div className='mt-4 flex w-auto items-center justify-between rounded-sm border border-gray-200'>
            <Button
              className='h-7'
              variant={null}
              onClick={() => selectedItem && handleUpdateQty(selectedProduct, selectedItem, -1)}
              disabled={
                !selectedItem ||
                (selectedItem.stock ?? 0) <= 0 ||
                getItemQty(selectedProduct.product_group_id, selectedItem.item_id) <= 0
              }
            >
              <MinusIcon className={cn('h-4 w-4 p-0')} />
            </Button>
            <span>{getItemQty(selectedProduct.product_group_id, selectedItem?.item_id || '')}</span>
            <Button
              className='h-7'
              variant={null}
              onClick={() => selectedItem && handleUpdateQty(selectedProduct, selectedItem, 1)}
              disabled={
                !selectedItem ||
                (selectedItem.stock ?? 0) <= 0 ||
                getItemQty(selectedProduct.product_group_id, selectedItem.item_id) >=
                  Math.min(selectedItem.stock ?? 0, maxQty) ||
                totalQty >= maxQty
              }
            >
              <PlusIcon className={cn('h-4 w-4 p-0')} />
            </Button>
          </div>
        </div>
      </div>
      <div className='flex h-full w-full max-w-full flex-col justify-between gap-3 px-6 py-4 lg:max-w-[70%]'>
        <div className='flex w-full flex-col gap-2'>
          <div className='relative w-full'>
            <Search size={16} className='absolute left-2 top-1/2 -translate-y-1/2 text-gray-500' />
            <input
              type='text'
              placeholder='Search variant...'
              className='w-full rounded border py-2 pl-8 pr-3 text-sm outline-none ring-gray-200 focus:ring-1 focus:ring-[--background]'
              value={params.q}
              onChange={handleSearch}
            />
          </div>
          {isLoading ? (
            <div className='grid grid-cols-4 gap-1'>
              {Array.from({ length: 8 }).map((_, idx) => (
                <Skeleton className='h-10 w-full' key={idx} />
              ))}
            </div>
          ) : (listProductItem?.length || 0) > 0 ? (
            <div className='flex w-full gap-3'>
              <ToggleGroup type='single' className='w-fit flex-wrap items-start justify-start'>
                {listProductItem?.map((item) => (
                  <ToggleGroupItem
                    key={item.item_id}
                    value={item.item_id}
                    disabled={
                      item.stock === 0 ||
                      (totalQty >= maxQty &&
                        getItemQty(selectedProduct.product_group_id, item.item_id) <= 0)
                    }
                    className={cn(
                      'max-h-[40px] min-w-[80px] gap-2 overflow-hidden break-words rounded border px-2 text-xs transition-colors',
                      'bg-[#F8FBFF] text-[#52535B]',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      isEmpty(colorScheme)
                        ? 'data-[state=on]:bg-primary data-[state=on]:text-white'
                        : 'hover:border-[var(--background)] hover:text-[--text-primary] data-[state=on]:border-[--btn-background] data-[state=on]:bg-[--btn-background] data-[state=on]:text-[var(--btn-label)]'
                    )}
                    onClick={() => handleClickItem(item)}
                  >
                    {truncateText(item.item_name as string, 30)}{' '}
                    {getItemQty(selectedProduct.product_group_id, item.item_id) > 0 &&
                      `(+${getItemQty(selectedProduct.product_group_id, item.item_id)})`}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
          ) : (
            <div className='flex w-full items-center justify-center text-sm text-gray-500'>
              No variants found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
